import React, { useEffect, useState } from 'react';

import PageLayout from 'components/layouts/PageLayout';
import styled from 'styled-components';
import Text from 'components/commons/Text';
import { useParams } from 'react-router-dom';
import { InputField, Row, Select, useModelStateContext } from 'saladsoft-primitive';
import { useStateContext } from 'providers/usePageState';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import { Switch } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import ServiceHelper from 'stores/ServiceHelper';
import { useStoreWrapper } from 'stores/StoreHelper';
import { toast } from 'react-toastify';
import Button from 'components/input/Button';
import ListDeletingForm from 'components/pages/listingSettings/ListDeletingForm';
import { AdminListingDto, AdminListingRequestDto } from 'services/data-contracts';
import { saveListing } from 'controllers/listingsController';
import { validationHelper } from 'controls/validationHelper';

const ListSettingsPage = () => {
  const { listingId } = useParams();
  const { listing, setListing } = useStateContext();
  const { uiStore } = useStoreWrapper();

  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [isValidWifi, setIsValidWifi] = useState(false);
  const [isValidHostawayId, setIsValidHostawayId] = useState(false);
  const { setState, setMultipleState, stateModel, serviceModel, updateServiceModelMultiple } =
    useModelStateContext<AdminListingRequestDto>();

  const handleSaveHostawayId = () => {
    if (serviceModel.current) {
      ServiceHelper.adminListingService?.syncHostawayListing(Number(listingId), {
        hostawayListingId: serviceModel.current.hostawayId,
      }).then((res: any) => {
        if (res.status === 200) {
          // setListing(res?.data);
          toast.success('Hostaway ID가 저장되었습니다.');
        } else {
          if (res.response.data.errorCode === 'LISTING.HOSTAWAY_ID_ALREADY_IN_USE') {
            toast.error('이미 다른 숙소에서 사용 중인 Hostaway ID입니다.');
          } else {
            toast.error(res.response.data.errorMessage);
          }
        }
      });
    }
  };

  const handleSaveWifi = () => {
    if (serviceModel.current) {
      ServiceHelper.adminListingService?.updateListing(Number(listingId), {
        ...listing,
        wifiUsername: serviceModel.current.wifiUsername,
        wifiPassword: serviceModel.current.wifiPassword,
      }).then((res: any) => {
        if (res.status === 200) {
          setListing(res?.data);
          toast.success('와이파이 설정이 저장되었습니다.');
        }
      });
    }
  };

  useEffect(() => {
    if (listing) {
      updateServiceModelMultiple(listing);
    }
  }, [listing]);



  const PageTitle = () => {
    return (
      <Row gap="10px">
        <Text type="H2">{listing?.localizedName?.ko} 설정</Text>
      </Row>
    );
  };

  const handleSave = async (businessId: number) => {
    const currentListing = listing;
    currentListing.business = { id: businessId };
    if (currentListing) {
      const updatedListing = (await saveListing(currentListing)) as AdminListingDto;
      setListing(updatedListing);
    }
  };
  const handleDeleteListing = () => {
    uiStore?.modal.show({
      style: { width: 400, height: 350 },
      title: '삭제확인',
      children: <ListDeletingForm listing={listing} />,
    });
  };

  const handleSetIsActive = (value: boolean) => {
    uiStore?.confirm.show({
      title: `'${listing?.localizedName?.ko}' 숙소를 ${value ? '운영중' : '대기중'}으로 변경하시겠습니까?`,
      onConfirmed: () => {
        ServiceHelper.adminListingService
          ?.updateListing(Number(listingId), {
            ...listing,
            isActive: value,
          })
          .then((res: any) => {
            if (res.status === 200) {
              setListing(res?.data);
              toast.success('숙소 노출 상태가 변경되었습니다.');
            }
          });
      },
    });
  };

  const handleSetIsRecommend = (value: boolean) => {
    ServiceHelper.adminListingService
      ?.updateListing(Number(listingId), {
        ...listing,
        isRecommend: value,
      })
      .then((res: any) => {
        if (res.status === 200) {
          setListing(res?.data);
          toast.success(`추천한옥으로 ${value ? '설정' : '해제'}되었습니다.`);
        }
      });
  };

  const handleSetIsNew = (value: boolean) => {
    ServiceHelper.adminListingService
      ?.updateListing(Number(listingId), {
        ...listing,
        isNew: value,
      })
      .then((res: any) => {
        if (res.status === 200) {
          setListing(res?.data);
          toast.success(`신규한옥으로 ${value ? '설정' : '해제'}되었습니다.`);
        }
      });
  };

  useEffect(() => {
    const getBusinesses = async () => {
      const res = await ServiceHelper?.adminBusinessService?.getBusinesses();
      const formattedBusinesses =
        res?.data
          ?.map((business: any) => ({
            key: business.id,
            value: business.name,
            isDefault: business.isDefault,
          }))
          .sort((a, b) => (b.isDefault ? 1 : a.isDefault ? -1 : 0)) || [];
      setBusinesses(formattedBusinesses);
    };

    getBusinesses();

    if (listing) {
      setMultipleState({
        wifiUsername: listing?.wifiUsername,
        wifiPassword: listing?.wifiPassword,
      });
    }
  }, [listing]);

  useEffect(() => {
    const wifiname = validationHelper.required(stateModel?.wifiUsername);
    const wifipassword = validationHelper.required(stateModel?.wifiPassword);
    setIsValidWifi(wifiname && wifipassword);
  }, [stateModel]);

  return (
    <PageLayout title={<PageTitle />}>
      <InnerSection style={{ minHeight: 'auto' }}>
        <InnerGroup>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              소유사업자 선택
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              시설의 소유사업자를 선택 해 주세요.
              <WarningText>
                *해당사업자의 MID를 이용해 결제가 되며, 숙소 상세 및 결제페이지의 푸터에 표시됩니다.
              </WarningText>
            </Text>
          </div>

          <Select
            placeHolder="사업자선택"
            items={businesses}
            defaultKey={listing?.business?.id}
            onChange={(value) => handleSave(value.key)}
          />
        </InnerGroup>
        <InnerGroup style={{ marginTop: 50 }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              운영 설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              시설의 운영 상태를 설정합니다.
              <br />
              대기중일 경우 노출되지 않습니다.
            </Text>
          </div>
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={listing?.isActive}
                  size="medium"
                  onChange={(e) => handleSetIsActive(e.target.checked)}
                />
              }
              label={listing?.isActive ? '운영중' : '운영 대기중'}
            />
          </div>
        </InnerGroup>
      </InnerSection>
      <InnerSection style={{ minHeight: 'auto' }}>
        <InnerGroup>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              와이파이설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              와이파이 설정을 합니다.
            </Text>
          </div>
          {listing && (
            <div>
              <div style={{ width: 300 }}>
                <InputField
                  label="와이파이 이름"
                  defaultValue={listing?.wifiUsername}
                  onChange={(value: string) => setState('wifiUsername', value)}
                />
              </div>
              <div style={{ width: 300, marginTop: 10 }}>
                <InputField
                  label="와이파이 비밀번호"
                  defaultValue={listing?.wifiPassword}
                  onChange={(value: string) => setState('wifiPassword', value)}
                />
              </div>
              <div style={{ width: 300, marginTop: 10 }}>
                <Button onClick={handleSaveWifi} style={{ width: 200 }} isValid={isValidWifi}>
                  저장
                </Button>
              </div>
            </div>
          )}
        </InnerGroup>
        <InnerGroup style={{ marginTop: 50 }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10, }}>
              Hostaway ID매핑
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              Hostaway ID를 매핑합니다.(숫자로만 이루어짐)
            </Text>
          </div>
          {listing && (
            <div>
              <div style={{ width: 300, marginTop: 10 }}>
                <InputField
                  type="number"
                  label="Hostaway ID"
                  defaultValue={listing?.hostawayId}
                  onChange={(value: string) => {
                    setState('hostawayId', Number(value));
                    setIsValidHostawayId(value.length > 5);
                  }}
                />
              </div>
              <div style={{ width: 300, marginTop: 10 }}>
                <Button onClick={handleSaveHostawayId} style={{ width: 200 }} isValid={isValidHostawayId}>
                  저장
                </Button>
              </div>
            </div>
          )}
        </InnerGroup>
      </InnerSection>
      <InnerSection style={{ minHeight: 'auto' }}>
        <InnerGroup>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              추천한옥 및 신규한옥 설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              시설의 추천한옥, 신규한옥 노출 여부를 설정합니다.
            </Text>
          </div>
          <div>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={listing?.isRecommend ?? false}
                    size="medium"
                    onChange={(e) => handleSetIsRecommend(e.target.checked)}
                  />
                }
                label={listing?.isRecommend ? '추천한옥' : '추천한옥 아님'}
              />
            </div>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={listing?.isNew ?? false}
                    size="medium"
                    onChange={(e) => handleSetIsNew(e.target.checked)}
                  />
                }
                label={listing?.isNew ? '신규한옥' : '신규한옥 아님'}
              />
            </div>
          </div>
        </InnerGroup>
      </InnerSection>
      <InnerSection style={{ minHeight: 'auto' }}>
        <InnerGroup>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              시설삭제
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION">
              등록된 시설을 삭제합니다.
            </Text>
          </div>
          <div>
            <Button onClick={handleDeleteListing} style={{ width: 200 }}>
              시설삭제
            </Button>
          </div>
        </InnerGroup>
      </InnerSection>
    </PageLayout>
  );
};

const WarningText = styled.div`
  color: var(--point-color);
  font-size: 12px;
`;

export default ListSettingsPage;
